<template>
  <el-dialog
    width="50%"
    :title="note && note.approved ? 'On-Air Details' : 'Approve for On-Air'"
    destroy-on-close
    :visible.sync="internalVisible"
    @close="handleClose"
  >
    <div v-if="note">
      <div class="modal-meta">
        <div class="meta-item">
          <i class="el-icon-user"></i>
          <span
            ><strong>{{ note.sender }}</strong></span
          >
        </div>
        <div class="meta-item" v-if="note.timestamp">
          <i class="el-icon-time"></i>
          <span>{{ formatDate(note.timestamp) }}</span>
        </div>
        <el-tag
          v-if="note.approved"
          size="mini"
          type="success"
          effect="dark"
          style="margin-left: auto"
        >
          <i class="fa fa-podcast"></i> On Air
        </el-tag>
        <el-tag v-if="note.flagged" size="mini" type="danger" effect="dark">
          <i class="fa fa-flag"></i> Flagged
        </el-tag>
      </div>

      <el-card class="transcript-card" shadow="never">
        <div slot="header" class="card-header">
          <i class="el-icon-document"></i> Transcript Preview
        </div>
        <span class="transcript-text">{{ previewText }}</span>
      </el-card>

      <div v-if="note.approved">
        <el-divider content-position="left"> <i class="fa fa-podcast"></i> On-Air Info </el-divider>

        <div class="approved-info">
          <div class="info-item" v-if="note.approvedBy">
            <span class="info-label">Approved by</span>
            <el-tag size="mini" type="success" effect="plain">
              <i class="el-icon-user"></i> {{ note.approvedBy }}
            </el-tag>
          </div>
          <div class="info-item" v-if="note.approvedAt">
            <span class="info-label">Approved on</span>
            <el-tag size="mini" type="info" effect="plain">
              <i class="el-icon-time"></i> {{ formatDate(note.approvedAt) }}
            </el-tag>
          </div>
          <div class="info-item" v-if="note.approveReason">
            <span class="info-label">Comment</span>
            <span class="info-reason">{{ note.approveReason }}</span>
          </div>
        </div>

        <el-alert type="info" :closable="false" show-icon style="margin-top: 16px">
          <span slot="title">
            To remove this voice note from on-air, go to the
            <strong>On-Air Panel</strong> on your dashboard.
          </span>
        </el-alert>
      </div>

      <div v-else>
        <el-divider />

        <el-form label-position="top" size="small">
          <el-form-item label="Approving As">
            <el-input :value="approvedBy" disabled>
              <i slot="prefix" class="el-icon-user el-input__icon"></i>
            </el-input>
          </el-form-item>

          <el-form-item label="Send On-Air To">
            <el-select
              v-model="computedOnAirUser"
              filterable
              placeholder="Select on-air user"
              style="width: 100%"
            >
              <el-option
                v-for="user in companyUsers"
                :key="user.id"
                :label="user.display_name"
                :value="user.id"
              >
                <span>{{ user.display_name }}</span>
                <span
                  v-if="user.id === me.id"
                  style="float: right; color: #67c23a; font-size: 12px"
                >
                  <i class="el-icon-user"></i> You
                </span>
              </el-option>
            </el-select>
          </el-form-item>

          <el-form-item label="Date of Approval">
            <el-input :value="approvalDate" disabled>
              <i slot="prefix" class="el-icon-date el-input__icon"></i>
            </el-input>
          </el-form-item>

          <el-form-item label="Reason / Comments (optional)">
            <el-input
              type="textarea"
              v-model="comments"
              placeholder="Add a reason or comment for this approval..."
              :rows="3"
              maxlength="300"
              show-word-limit
            />
          </el-form-item>
        </el-form>
      </div>
    </div>

    <div slot="footer" class="dialog-footer">
      <el-button size="small" @click="handleClose">
        {{ note && note.approved ? 'Close' : 'Cancel' }}
      </el-button>
      <el-button
        v-if="note && !note.approved"
        size="small"
        type="primary"
        :loading="loading"
        @click="handleApprove"
      >
        Approve &amp; Send On-Air
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import moment from 'moment';
import { mapState } from 'vuex';

export default {
  name: 'ApproveModal',
  props: {
    visible: { type: Boolean, required: true },
    note: { type: Object, default: null },
    loading: { type: Boolean, default: false },
  },
  data() {
    return {
      comments: '',
      selectedOnAirUser: null,
    };
  },
  computed: {
    ...mapState('user', ['companyUsers', 'me']),
    internalVisible: {
      get() {
        return this.visible;
      },
      set(v) {
        this.$emit('update:visible', v);
      },
    },
    computedOnAirUser: {
      get() {
        return this.selectedOnAirUser || null;
      },
      set(onAirUser) {
        this.selectedOnAirUser = onAirUser;
      },
    },
    approvedBy() {
      return this.me ? this.me.display_name : '';
    },
    approvalDate() {
      return moment().format('DD MMM YYYY, HH:mm');
    },
    previewText() {
      if (!this.note || !this.note.transcript) return 'No transcript available.';
      return this.note.transcript.length > 150
        ? `${this.note.transcript.slice(0, 150)}...`
        : this.note.transcript;
    },
  },
  mounted() {
    this.$store.dispatch('user/getCompanyUsers');
  },
  methods: {
    formatDate(timestamp) {
      return moment(timestamp).format('DD MMM YYYY, HH:mm');
    },
    handleApprove() {
      const userId = this.computedOnAirUser;
      if (!userId) {
        this.$notify.warning('Please select an on-air user');
        return;
      }

      this.$emit('approve', {
        id: this.note.id,
        approvedBy: this.me.display_name,
        approvedById: this.me.id,
        approveStatus: true,
        reason: this.comments,
      });

      this.$store.dispatch('interaction/sendToOnAir', {
        interactionId: this.note.id,
        userId,
      });

      if (this.comments && this.comments.trim()) {
        this.$store.dispatch('interaction/sendComment', {
          interactionId: this.note.id,
          message: this.comments,
        });
      }
    },
    handleClose() {
      this.comments = '';
      this.selectedOnAirUser = null;
      this.internalVisible = false;
      this.$emit('close');
    },
  },
};
</script>

<style scoped>
.modal-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  padding: 12px;
  background: #f9f9f9;
  border-radius: 6px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: #606266;
}

.transcript-card {
  margin-bottom: 16px;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #606266;
}

.transcript-text {
  font-size: 13px;
  line-height: 1.6;
  color: #606266;
  font-style: italic;
}

.approved-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background: #f0fff4;
  border: 1px solid #b7ebc8;
  border-radius: 6px;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-label {
  font-size: 12px;
  color: #909399;
  min-width: 90px;
}

.info-reason {
  font-size: 13px;
  color: #606266;
  font-style: italic;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
</style>


// WEBPACK FOOTER //
// ApproveModal.vue?42213344